import { useMemo, useState } from "react";
import { SeverityBadge } from "@/components/intel/SeverityBadge";
import { TIMELINE_CATEGORY_LABEL, type TimelineCategory, type TimelineEvent } from "@/lib/intel/mock";
import { Activity, ExternalLink } from "lucide-react";

function normalize(e: any, i: number): TimelineEvent {
  return {
    id: e?.id ?? `t-${i}`,
    ts: String(e?.ts ?? ""),
    title: String(e?.title ?? e?.event ?? e?.description ?? "Event"),
    source: String(e?.source ?? "unknown"),
    severity: (["low", "medium", "high", "critical"].includes(e?.severity) ? e.severity : "low"),
    category: (e?.category ?? undefined) as TimelineCategory | undefined,
    precision: e?.precision,
    kind: e?.kind ?? (e?.category === "run" ? "run" : "evidence"),
    ref: e?.ref,
  };
}

function fmt(ts: string, precision?: TimelineEvent["precision"]) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  if (precision === "year") return String(d.getUTCFullYear());
  if (precision === "month") return d.toISOString().slice(0, 7);
  return d.toISOString().slice(0, 10);
}

export function EvidenceTimeline({
  events,
  emptyLabel = "No dated evidence recovered yet.",
}: {
  events: unknown[];
  emptyLabel?: string;
}) {
  const all = useMemo(() => (events ?? []).map(normalize).filter((e) => e.ts), [events]);
  const evidence = useMemo(() => all.filter((e) => e.kind !== "run"), [all]);
  const runRows = useMemo(() => all.filter((e) => e.kind === "run"), [all]);

  const categories = useMemo(() => {
    const set = new Map<TimelineCategory, number>();
    for (const e of evidence) {
      const c = (e.category ?? "document") as TimelineCategory;
      set.set(c, (set.get(c) ?? 0) + 1);
    }
    return [...set.entries()].sort((a, b) => b[1] - a[1]);
  }, [evidence]);

  const [active, setActive] = useState<TimelineCategory | "all">("all");
  const [showRun, setShowRun] = useState(false);

  const visible = useMemo(() => {
    const base = active === "all" ? evidence : evidence.filter((e) => (e.category ?? "document") === active);
    const rows = showRun ? [...base, ...runRows] : base;
    return rows.sort((a, b) => a.ts.localeCompare(b.ts));
  }, [evidence, runRows, active, showRun]);

  const grouped = useMemo(() => {
    const map = new Map<string, TimelineEvent[]>();
    for (const e of visible) {
      const year = e.ts.slice(0, 4);
      const arr = map.get(year) ?? [];
      arr.push(e);
      map.set(year, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setActive("all")}
          className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition ${active === "all" ? "border-cyan text-cyan" : "border-panel-border text-muted-foreground hover:text-foreground"}`}
        >
          All · {evidence.length}
        </button>
        {categories.map(([c, n]) => (
          <button
            key={c}
            type="button"
            onClick={() => setActive(c)}
            className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition ${active === c ? "border-cyan text-cyan" : "border-panel-border text-muted-foreground hover:text-foreground"}`}
          >
            {TIMELINE_CATEGORY_LABEL[c] ?? c} · {n}
          </button>
        ))}
        {runRows.length > 0 && (
          <button
            type="button"
            onClick={() => setShowRun((v) => !v)}
            className={`ml-auto rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition ${showRun ? "border-amber text-amber" : "border-panel-border text-muted-foreground hover:text-foreground"}`}
          >
            Run activity {showRun ? "on" : "off"}
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-md border border-dashed border-panel-border p-8 text-center text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([year, rows]) => (
            <div key={year}>
              <div className="mb-2 flex items-center gap-2">
                <span className="font-display text-lg text-cyan">{year}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{rows.length} event{rows.length === 1 ? "" : "s"}</span>
                <div className="h-px flex-1 bg-panel-border" />
              </div>
              <div className="relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-px bg-panel-border" />
                <div className="space-y-2">
                  {rows.map((e, i) => (
                    <div key={`${e.id}-${i}`} className="relative">
                      <div className={`absolute -left-[18px] top-3 h-2.5 w-2.5 rounded-full ring-2 bg-background ${e.kind === "run" ? "ring-amber" : "ring-cyan"}`} />
                      <div className={`rounded-md border border-panel-border bg-background/40 p-2.5 ${e.kind === "run" ? "opacity-70" : ""}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-[11px] text-cyan">{fmt(e.ts, e.precision)}</span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {TIMELINE_CATEGORY_LABEL[(e.category ?? "document") as TimelineCategory] ?? e.category ?? "evidence"}
                          </span>
                          {e.precision && e.precision !== "day" && (
                            <span className="font-mono text-[10px] text-muted-foreground">± {e.precision}</span>
                          )}
                          <SeverityBadge s={e.severity} />
                          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                            <Activity className="mr-1 inline h-3 w-3" />{e.source}
                          </span>
                        </div>
                        <div className="mt-1 text-sm break-words">{e.title}</div>
                        {e.ref && (
                          <a
                            href={e.ref}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] text-cyan hover:underline break-all"
                          >
                            <ExternalLink className="h-3 w-3" /> {e.ref.slice(0, 90)}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}